import { isISODate, timelineRange, addDays } from '../shared/dates.mjs';
import { syncGroups } from '../shared/groups.mjs';
import { ZOOM_DAYS, COLORS as COLOR_LIST, SIDEBAR_WIDTHS, LIMITS } from '../shared/limits.mjs';
import { COLOR_MODES, ownStatus } from '../shared/status.mjs';

const FEEDBACK_KINDS = new Set(['bug', 'idea']);
const COLORS = new Set(COLOR_LIST);
const HEIGHTS = new Set(['compact', 'comfortable', 'tall']);
const JIRA_KEY_PATTERN = /^[A-Z][A-Z0-9_]*-\d+$/;
const FEEDBACK_IMAGE_MAX = 200000;

// Characters XML cannot hold (the PNG export is an SVG): C0 controls other than
// tab and newline, U+FFFE/U+FFFF, and unpaired surrogates (also left by slicing).
const XML_INVALID = /[\u0000-\u0008\u000B-\u001F\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
const text = (value, length) => String(value || '').trim().slice(0, length).replace(XML_INVALID, '');
// A screenshot is only ever a plain base64 image data URL; anything else is dropped.
const IMAGE_DATA_URL = /^data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+={0,2}$/;
const imageDataUrl = (value) => (typeof value === 'string' && value.length <= FEEDBACK_IMAGE_MAX && IMAGE_DATA_URL.test(value) ? value : '');
const color = (value, fallback = 'violet') => (COLORS.has(value) ? value : fallback);
const jiraKey = (value) => {
  const candidate = String(value || '').trim().toUpperCase().slice(0, 40);
  return JIRA_KEY_PATTERN.test(candidate) ? candidate : '';
};
const dateOnly = (value) => {
  const valueString = String(value || '').slice(0, 10);
  return isISODate(valueString) ? valueString : '';
};
const isoStamp = (value) => {
  const parsed = Date.parse(String(value || ''));
  return Number.isNaN(parsed) ? '' : new Date(parsed).toISOString();
};
const sanitizeHistory = (value) => (Array.isArray(value) ? value : [])
  .map((entry) => ({
    id: id(entry?.id, 'change'),
    at: isoStamp(entry?.at),
    summary: text(entry?.summary, 80) || 'Roadmap edited',
    by: text(entry?.by, 80),
  }))
  .filter((entry) => entry.at)
  .slice(-LIMITS.history);
const id = (value, prefix) => {
  const normalized = String(value || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
  return normalized || `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};
const defaultStart = () => {
  const today = new Date();
  const mondayOffset = (today.getUTCDay() + 6) % 7;
  today.setUTCDate(today.getUTCDate() - mondayOffset - 14);
  return today.toISOString().slice(0, 10);
};

const defaultBoard = () => {
  const startDate = defaultStart();
  return {
    schemaVersion: 1,
    revision: 0,
    title: 'Untitled roadmap',
    startDate,
    endDate: addDays(startDate, ZOOM_DAYS.weeks - 1),
    zoom: 'weeks',
    dayCount: ZOOM_DAYS.weeks,
    height: 'comfortable',
    sidebarWidth: 264,
    lanes: [],
    items: [],
    milestones: [],
    updatedAt: '',
    updatedBy: '',
    createdBy: '',
    createdByAccountId: '',
    history: [],
    locked: false,
    colorMode: 'standard',
    autoSyncTaskFields: false,
    milestonesHidden: false,
    lockedBy: '',
    lockedByAccountId: '',
  };
};

const normalizeBoard = (candidate, revision = 0) => {
  const source = candidate && typeof candidate === 'object' ? candidate : {};
  const fallback = defaultBoard();
  const startDate = dateOnly(source.startDate) || fallback.startDate;
  const lanes = (Array.isArray(source.lanes) ? source.lanes : fallback.lanes).slice(0, LIMITS.lanes).map((lane, index) => ({
    id: id(lane?.id, 'lane'),
    title: text(lane?.title, 120) || `Lane ${index + 1}`,
    color: color(lane?.color, ['violet', 'blue', 'teal', 'amber'][index % 4]),
    collapsed: Boolean(lane?.collapsed),
    hidden: Boolean(lane?.hidden),
    // Nautilus: optional link to a Nautilus project.
    projectId: text(lane?.projectId, 80),
  }));
  const laneIds = new Set(lanes.map((lane) => lane.id));
  const items = (Array.isArray(source.items) ? source.items : fallback.items)
    .slice(0, LIMITS.items)
    .map((item) => ({
      id: id(item?.id, 'item'),
      laneId: laneIds.has(item?.laneId) ? item.laneId : lanes[0]?.id,
      title: text(item?.title, 120) || 'Untitled item',
      startDate: dateOnly(item?.startDate) || startDate,
      endDate: dateOnly(item?.endDate) || dateOnly(item?.startDate) || startDate,
      color: color(item?.color, 'blue'),
      status: ownStatus(item),
      description: text(item?.description, 1000),
      jiraKey: jiraKey(item?.jiraKey),
      // Nautilus: optional link and last-agreed date snapshot for a Nautilus task.
      taskId: text(item?.taskId, 80),
      taskStart: dateOnly(item?.taskStart) || '',
      taskEnd: dateOnly(item?.taskEnd) || '',
      datesAt: isoStamp(item?.datesAt),
      dashed: Boolean(item?.dashed),
      row: Number.isInteger(item?.row) && item.row >= 0 && item.row <= 7 ? item.row : null,
      noStart: Boolean(item?.noStart),
      isGroup: Boolean(item?.isGroup),
      // Groups store their own planned dates separately from the child-expanded
      // dates drawn on the timeline. Older saved boards simply use their
      // previous rendered dates as the starting plan.
      ...(item?.isGroup ? {
        ownStartDate: dateOnly(item?.ownStartDate) || dateOnly(item?.startDate) || startDate,
        ownEndDate: dateOnly(item?.ownEndDate) || dateOnly(item?.endDate) || dateOnly(item?.startDate) || startDate,
      } : {}),
      // Only on collapsed groups, so the flag costs no storage on every other item.
      ...(item?.isGroup && item?.collapsed ? { collapsed: true } : {}),
      parentId: String(item?.parentId || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80),
    }))
    .filter((item) => item.laneId)
    .map((item) => item.noStart ? { ...item, startDate: item.endDate } : (item.endDate < item.startDate ? { ...item, endDate: item.startDate } : item));
  syncGroups(items);
  const milestones = (Array.isArray(source.milestones) ? source.milestones : fallback.milestones).slice(0, LIMITS.milestones).map((milestone, index) => ({
    id: id(milestone?.id, 'milestone'),
    title: text(milestone?.title, 80) || 'Milestone',
    date: dateOnly(milestone?.date) || startDate,
    color: color(milestone?.color, 'coral'),
    row: Number(milestone?.row) === 1 ? 1 : Number(milestone?.row) === 0 ? 0 : index % 2,
  }));
  const zoom = Object.hasOwn(ZOOM_DAYS, source.zoom) ? source.zoom : 'weeks';
  const requestedSidebarWidth = Number(source.sidebarWidth) || fallback.sidebarWidth;
  const sidebarWidth = SIDEBAR_WIDTHS.reduce((closest, candidateWidth) =>
    Math.abs(candidateWidth - requestedSidebarWidth) < Math.abs(closest - requestedSidebarWidth) ? candidateWidth : closest
  );
  return {
    schemaVersion: 1,
    revision: Math.max(0, Number(revision) || 0),
    title: text(source.title, 120) || 'Program roadmap',
    startDate,
    zoom,
    ...timelineRange({ ...source, startDate }, ZOOM_DAYS[zoom]),
    height: HEIGHTS.has(source.height) ? source.height : 'comfortable',
    sidebarWidth,
    lanes,
    items,
    milestones,
    updatedAt: text(source.updatedAt, 30),
    updatedBy: text(source.updatedBy, 80),
    createdBy: text(source.createdBy, 80),
    createdByAccountId: text(source.createdByAccountId, 128),
    history: sanitizeHistory(source.history),
    locked: Boolean(source.locked),
    colorMode: COLOR_MODES.includes(source.colorMode) ? source.colorMode : 'standard',
    autoSyncTaskFields: source.autoSyncTaskFields === true,
    milestonesHidden: Boolean(source.milestonesHidden),
    lockedBy: text(source.lockedBy, 80),
    lockedByAccountId: text(source.lockedByAccountId, 128),
  };
};


export { defaultBoard, normalizeBoard, text, jiraKey, dateOnly, isoStamp, imageDataUrl, LIMITS, FEEDBACK_KINDS, FEEDBACK_IMAGE_MAX, ZOOM_DAYS };
